"use client";

import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { ScrollArea } from "@/components/ui/ScrollArea";
import { Spinner } from "@/components/ui/Spinner";
import type { useReactTable } from "@tanstack/react-table";
import { CircleSlash, RotateCcw } from "lucide-react";
import type { RefObject } from "react";
import { useId } from "react";
import cn from "twsx";
import styles from "./List.module.css";
import { ListRowLink, SelectionCheckbox } from "./FilterListRow";
import { isInteractiveListRowTarget } from "./listRowInteraction";

/** The board view: one card per row in a responsive grid. */
export function FilterListGridSurface<$Row>({
  table,
  isLoading,
  enableRowSelection,
  getRowHref,
  getRowTarget,
  isRowClickable,
  onClickRow,
  handleRowSelect,
  card,
  bottomRef,
  viewportRef,
  error,
  isRetrying,
  onRetry,
  isFetchingMore
}: {
  table: ReturnType<typeof useReactTable<$Row>>;
  isLoading?: boolean;
  enableRowSelection: boolean;
  getRowHref?: (row: $Row) => string | undefined;
  getRowTarget?: (row: $Row) => React.HTMLAttributeAnchorTarget | undefined;
  isRowClickable?: (row: $Row) => boolean;
  onClickRow?: (row: $Row) => void;
  handleRowSelect: (clickedRowIndex: number, shiftKey: boolean) => void;
  card?: (row: $Row) => React.ReactNode;
  bottomRef: RefObject<HTMLDivElement>;
  viewportRef: RefObject<HTMLDivElement>;
  error?: unknown;
  isRetrying?: boolean;
  onRetry?: () => void;
  isFetchingMore?: boolean;
}) {
  "use no memo";

  const descriptionPrefix = useId();
  return <ScrollArea className="flex-1 min-h-0 w-full overflow-auto" viewportRef={viewportRef}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
        {isLoading ? <div role="status" className="col-span-full flex justify-center py-20">
              <Spinner aria-label="Loading results" />
            </div> : table.getRowModel().rows.length > 0 ? table.getRowModel().rows.map((row, rowIndex) => {
        const clickable = isRowClickable?.(row.original) ?? true;
        const href = clickable ? getRowHref?.(row.original) : undefined;
        const descriptionId = `${descriptionPrefix}-${row.id}`;
        return <div key={row.id} className={cn("relative group/grid-card", row.getIsSelected() && "ring-2 ring-blue-light", href && styles.linked)} onClick={href || !clickable ? undefined : event => {
          if (!isInteractiveListRowTarget(event.target)) onClickRow?.(row.original);
        }}>
                  {href && <ListRowLink href={href} target={getRowTarget?.(row.original)} label={`Open card ${rowIndex + 1}`} descriptionId={descriptionId} />}
                  {enableRowSelection && row.getCanSelect() && <div className="absolute top-2 left-2 z-10">
                      <SelectionCheckbox ariaLabel={`Select card ${rowIndex + 1}`} checked={row.getIsSelected()} onClick={event_0 => handleRowSelect(rowIndex, event_0.shiftKey)} />
                    </div>}
                  <div id={descriptionId} className={href ? styles.cardContent : undefined}>{card?.(row.original)}</div>
                </div>;
      }) : error ? <div className="col-span-full flex flex-col items-center justify-center py-20">
              <EmptyState tone="danger" icon={CircleSlash} title="Couldn&apos;t load results" description="The list could not be loaded. Try again without losing your filters." action={onRetry ? <Button disabled={isRetrying} onClick={onRetry} variant="danger">
                      <RotateCcw />
                      {isRetrying ? "Retrying..." : "Retry"}
                    </Button> : undefined} />
            </div> : <div className="col-span-full flex flex-col items-center justify-center py-20">
              <EmptyState icon={CircleSlash} title="No results" description="No results found for your search" />
            </div>}
        <div ref={bottomRef} className={styles.clearance} />
        {isFetchingMore && <div className="col-span-full flex flex-row justify-center py-4">
            <Spinner size="md" tone="faint" />
          </div>}
      </div>
    </ScrollArea>;
}